import { zodResolver } from "@hookform/resolvers/zod";
import { useLocalSearchParams, useNavigation } from "expo-router";
import { useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Alert, StyleSheet, TextInput, View } from "react-native";

import { CustomHeader } from "@/components/CustomHeader";
import { ThemedText } from "@/components/ThemedText";
import { ThemedView } from "@/components/ThemedView";
import { Button } from "@/components/ui/Button";
import Dropdown, { type DropdownOption } from "@/components/ui/Dropdown.native";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { COUNTRIES } from "@/constants/countries";
import { useAuth } from "@/context/AuthContext";
import { resolveError } from "@/validation/messages";
import type {
  InviteErrorCode,
  InviteIdentifier,
} from "@/utils/groupInvite.native";
import { sendGroupInvitation } from "@/utils/groupInvite.native";

import { inviteSchema, type InviteFormValues } from "./invite.schema";

type InviteMode = InviteFormValues["type"];

const errorCodeToI18nKey: Record<InviteErrorCode, string> = {
  USER_NOT_FOUND: "USER_NOT_FOUND",
  USER_ALREADY_INVITED: "USER_ALREADY_INVITED",
  USER_ALREADY_MEMBER: "USER_ALREADY_MEMBER",
  CANNOT_INVITE_YOURSELF: "CANNOT_INVITE_YOURSELF",
  INVITE_WRITE_FAILED: "FAILED_TO_SEND_INVITATION",
};

const phoneCountries = COUNTRIES.filter((c) => "phonePrefix" in c && c.phonePrefix);
const defaultPhonePrefix =
  (phoneCountries[0] as { phonePrefix?: string })?.phonePrefix ?? "";

const defaultsForMode = (mode: InviteMode): InviteFormValues => {
  if (mode === "email") return { type: "email", email: "" };
  if (mode === "username") return { type: "username", username: "" };
  return {
    type: "phone",
    phone: { prefix: defaultPhonePrefix, number: "" },
  };
};

const buildIdentifier = (values: InviteFormValues): InviteIdentifier => {
  if (values.type === "email") return { type: "email", value: values.email };
  if (values.type === "username")
    return { type: "username", value: values.username };
  return {
    type: "phone",
    prefix: values.phone.prefix,
    number: values.phone.number,
  };
};

export default function InviteUserScreen() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigation = useNavigation();
  const { groupId, groupName, group } = useLocalSearchParams<{
    groupId: string;
    groupName: string;
    group?: string;
  }>();

  const memberIds = useMemo(() => {
    if (!group) return [] as string[];
    try {
      const parsed = JSON.parse(group) as { memberIds?: unknown };
      return Array.isArray(parsed?.memberIds)
        ? (parsed.memberIds as string[])
        : [];
    } catch {
      return [];
    }
  }, [group]);

  const [mode, setMode] = useState<InviteMode>("email");
  const [loading, setLoading] = useState(false);

  const { control, handleSubmit, reset, formState } = useForm<InviteFormValues>({
    mode: "onTouched",
    resolver: zodResolver(inviteSchema) as any,
    defaultValues: defaultsForMode("email"),
  });

  const switchMode = (next: InviteMode) => {
    if (next === mode) return;
    setMode(next);
    reset(defaultsForMode(next));
  };

  const watched = useWatch({ control }) as Partial<{
    email: string;
    username: string;
    phone: { prefix?: string; number?: string };
  }>;
  const hasRequiredInput =
    mode === "email"
      ? !!watched?.email?.trim()
      : mode === "username"
        ? !!watched?.username?.trim()
        : !!watched?.phone?.number?.trim();

  const phoneCountry =
    (phoneCountries[0] as {
      phonePlaceholder?: string;
      phoneMaxLength?: number;
      phoneNumberCheck?: (v: string) => string;
    }) ?? undefined;
  const prefixOptions: DropdownOption[] = phoneCountries.map((c) => ({
    value: (c as { phonePrefix: string }).phonePrefix,
    label: (c as { phonePrefix: string }).phonePrefix,
    icon: c.flag,
  }));

  const onSubmit = handleSubmit(async (values) => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const result = await sendGroupInvitation({
        groupId,
        groupName,
        inviterUid: user.uid,
        memberIds,
        identifier: buildIdentifier(values),
      });
      if (result.kind === "err") {
        Alert.alert(t("ERROR"), t(errorCodeToI18nKey[result.code]));
        return;
      }
      Alert.alert(t("SUCCESS"), t("INVITATION_SENT_SUCCESSFULLY"));
      navigation.goBack();
    } catch (err) {
      console.error("Error sending invitation: ", err);
      Alert.alert(t("ERROR"), t("FAILED_TO_SEND_INVITATION"));
    } finally {
      setLoading(false);
    }
  });

  return (
    <>
      <CustomHeader title={t("INVITE_USERS")} type="subtitle" showBackButton />
      <ThemedView style={styles.container}>
        <ThemedText style={styles.label}>
          {t("INVITE_USER_TO", { groupName })}
        </ThemedText>

        <View style={{ marginBottom: 16 }}>
          <SegmentedControl<InviteMode>
            testID="invite-mode"
            value={mode}
            onChange={switchMode}
            options={[
              { value: "email", label: t("INVITE_BY_EMAIL") },
              { value: "username", label: t("INVITE_BY_USERNAME") },
              { value: "phone", label: t("INVITE_BY_PHONE") },
            ]}
          />
        </View>

        {mode === "email" && (
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <>
                <TextInput
                  style={styles.input}
                  placeholder={t("ENTER_USER_EMAIL")}
                  placeholderTextColor="#9CA3AF"
                  value={(field.value as string) ?? ""}
                  onChangeText={field.onChange}
                  onBlur={field.onBlur}
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoComplete="email"
                />
                {fieldState.error && (
                  <ThemedText style={styles.errorText}>
                    {resolveError(fieldState.error, t)}
                  </ThemedText>
                )}
              </>
            )}
          />
        )}

        {mode === "username" && (
          <Controller
            control={control}
            name="username"
            render={({ field, fieldState }) => (
              <>
                <TextInput
                  style={styles.input}
                  placeholder={t("ENTER_USERNAME")}
                  placeholderTextColor="#9CA3AF"
                  value={(field.value as string) ?? ""}
                  onChangeText={field.onChange}
                  onBlur={field.onBlur}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
                {fieldState.error && (
                  <ThemedText style={styles.errorText}>
                    {resolveError(fieldState.error, t)}
                  </ThemedText>
                )}
              </>
            )}
          />
        )}

        {mode === "phone" && (
          <View>
            <Controller
              control={control}
              name="phone.prefix"
              render={({ field }) => (
                <View style={{ marginBottom: 8 }}>
                  <Dropdown
                    value={(field.value as string) ?? defaultPhonePrefix}
                    onChange={field.onChange}
                    placeholder={t("PLEASE_SELECT_COUNTRY")}
                    options={prefixOptions}
                  />
                </View>
              )}
            />
            <Controller
              control={control}
              name="phone.number"
              render={({ field }) => (
                <TextInput
                  style={styles.input}
                  placeholder={
                    phoneCountry?.phonePlaceholder ?? t("PHONE_PLACEHOLDER")
                  }
                  placeholderTextColor="#9CA3AF"
                  value={(field.value as string) ?? ""}
                  onChangeText={(text) => {
                    const normalized = phoneCountry?.phoneNumberCheck
                      ? phoneCountry.phoneNumberCheck(text.replace(/\D/g, ""))
                      : text.replace(/\D/g, "");
                    field.onChange(normalized);
                  }}
                  onBlur={field.onBlur}
                  keyboardType="phone-pad"
                  maxLength={phoneCountry?.phoneMaxLength ?? 15}
                  autoComplete="tel"
                />
              )}
            />
            {(() => {
              const phoneErrs = (formState.errors as any).phone as
                | { prefix?: { message?: string }; number?: { message?: string } }
                | undefined;
              const err = phoneErrs?.prefix ?? phoneErrs?.number;
              if (!err?.message) return null;
              return (
                <ThemedText style={styles.errorText}>
                  {resolveError(err as any, t)}
                </ThemedText>
              );
            })()}
          </View>
        )}

        {loading ? (
          <ActivityIndicator style={{ marginTop: 16 }} />
        ) : (
          <Button
            text={t("INVITE_USER")}
            onPress={onSubmit}
            disabled={!hasRequiredInput}
            style={{ width: "100%", maxWidth: "100%", marginTop: 16 }}
          />
        )}
      </ThemedView>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },
  label: {
    fontSize: 16,
    marginBottom: 8,
  },
  input: {
    width: "100%",
    height: 48,
    borderColor: "#ccc",
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    marginBottom: 8,
    fontSize: 16,
    backgroundColor: "#fafafa",
    color: "#000",
  },
  errorText: {
    color: "#dc2626",
    fontSize: 13,
    marginBottom: 8,
  },
});
